import React from 'react';
import {
    useProperty,
    usePropertyComplementaryData,
    usePropertyFetching,
    usePropertyValidation,
} from '../../../contextsProperty/ContextProperties';
import TextInput from '../../../../muiComponents/TextInput/TextInputV2';
import SwitchInput from '../../../../muiComponents/switchInput/SwitchInput';
import Card from '../../../../muiComponents/Card';
import InfoIcon from '../../../../components/InfoIcon';
import useTranslate from '../../../../customHooks/useTranslate';
import { getChildrenAgeSegmentSortOrder } from '../../../../utils/property/propertyUtils';
import { PROGRESS_PUBLIC_PORTRAIT_TAB } from '../../../../types/properties/propertyTypes';
import AgeRangesTable from './AgeRangesTable';
import '../../../../stylesheets/stylesLayout/yourProperties.scss';
import './ageRanges.scss';
import LoadingTab from '../LoadingTab';
import Title from '../Title';

export default function KidPolicies() {
    const {
        primary,
        adultsOnly,
        adultAge,
        childrenAgeSegments,
        handleInputChange,
        updateChildrenAgeSegmentAge,
        addChildrenAgeSegment,
        deleteChildrenAgeSegment,
    } = useProperty();
    const { childrenAgeSegmentsList } = usePropertyComplementaryData();
    const { isFetching } = usePropertyFetching();
    const { errors, handleValidate } = usePropertyValidation();
    const translate = useTranslate();
    const validateAgeRanges = () =>
        handleValidate(PROGRESS_PUBLIC_PORTRAIT_TAB.KID_POLICIES);

    const activeAgeSegments = childrenAgeSegments.filter(
        segment => !segment.Deleted
    );
    const orderedAgeSegments = [...activeAgeSegments].sort(
        (first, second) =>
            getChildrenAgeSegmentSortOrder(first) -
                getChildrenAgeSegmentSortOrder(second) ||
            first.name.localeCompare(second.name)
    );
    const availableAgeSegments = childrenAgeSegmentsList.filter(
        option => !activeAgeSegments.some(segment => segment.key === option.key)
    );
    const lastAge = orderedAgeSegments[orderedAgeSegments.length - 1]?.age || 0;
    const canAddRange =
        availableAgeSegments.length > 0 &&
        primary &&
        adultAge > 0 &&
        lastAge < adultAge;

    const addAgeRange = () => {
        const nextSegment = availableAgeSegments[0];
        if (nextSegment) {
            addChildrenAgeSegment(nextSegment, adultAge);
        }
    };

    if (isFetching) {
        return <LoadingTab />;
    }

    return (
        <div className='your-property__container'>
            <Title
                as='h1'
                text={'kids_policies_title'}
                className='text-align-center'
            />
            <Card className='your-properties__form'>
                <SwitchInput
                    checked={adultsOnly}
                    onChangeMethod={e =>
                        handleInputChange('adultsOnly', e.target.checked)
                    }
                    labelText='adults_only'
                    disabled={!primary}
                    style={{ marginBottom: '40px' }}
                />

                <TextInput
                    labelText={'age_adult_mob'}
                    handleChange={e =>
                        handleInputChange('adultAge', parseInt(e.target.value))
                    }
                    onBlur={validateAgeRanges}
                    value={adultAge}
                    name='adultAge'
                    type='number'
                    disabled={!primary}
                    variant={!primary ? 'filled' : 'outlined'}
                />
                {!adultsOnly && (
                    <section className='age-ranges'>
                        <header className='age-ranges__header'>
                            <h2>
                                {translate('age_ranges')}{' '}
                                <InfoIcon tooltipText='age_ranges_info' />
                            </h2>
                            <span className='age-ranges-table__secondary-text'>{`${
                                orderedAgeSegments.length
                            } ${translate('of')} ${
                                childrenAgeSegmentsList.length
                            }`}</span>
                        </header>

                        <AgeRangesTable
                            adultAge={adultAge}
                            availableCount={availableAgeSegments.length}
                            canAddRange={canAddRange}
                            disabled={!primary}
                            onAddRange={addAgeRange}
                            onAgeBlur={validateAgeRanges}
                            onDeleteRange={deleteChildrenAgeSegment}
                            onUpdateAge={updateChildrenAgeSegmentAge}
                            segments={orderedAgeSegments}
                        />
                        {errors.childrenAgeSegments && (
                            <p className='age-ranges__error' role='alert'>
                                {translate(errors.childrenAgeSegments)}
                            </p>
                        )}
                    </section>
                )}
            </Card>
        </div>
    );
}
